import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Button } from '../ui';
import Badge, { WinnerBadge } from '../ui/Badge';
import { Section, EmptyState } from './ActiveEntriesSection';

/**
 * WinsSection - Shows user's winning entries
 *
 * Displays won items with handover status and quick actions.
 */
export default function WinsSection({ entries, raffles, pendingHandovers, className }) {
  // Filter to only winning entries
  const wonEntries = entries.filter(e => e.isWinner === true);

  if (wonEntries.length === 0) {
    return (
      <Section title="Your Wins" className={className}>
        <EmptyState
          icon={TrophyIcon}
          title="No wins yet"
          description="Enter competitions and test your skills to win great items."
        />
      </Section>
    );
  }

  // Sort: pending handovers first, then by date
  const sortedWins = [...wonEntries].sort((a, b) => {
    const aHandover = pendingHandovers.find(h => h.raffleId === a.raffleId);
    const bHandover = pendingHandovers.find(h => h.raffleId === b.raffleId);

    // Pending handovers first
    if (aHandover && !bHandover) return -1;
    if (!aHandover && bHandover) return 1;

    // Then by date
    return b.createdAt?.seconds - a.createdAt?.seconds;
  });

  return (
    <Section
      title="Your Wins"
      count={wonEntries.length}
      actionLabel="View All"
      actionHref="/my-entries?status=won"
      className={className}
    >
      <div className="space-y-3">
        {sortedWins.slice(0, 5).map((entry) => {
          const raffle = raffles[entry.raffleId];
          const handover = pendingHandovers.find(h => h.raffleId === entry.raffleId);
          return (
            <WinCard key={entry.id} entry={entry} raffle={raffle} handover={handover} />
          );
        })}
      </div>
      {wonEntries.length > 5 && (
        <p className="text-sm text-cream/50 text-center mt-4">
          +{wonEntries.length - 5} more wins
        </p>
      )}
    </Section>
  );
}

function WinCard({ entry, raffle, handover }) {
  if (!raffle) {
    return (
      <div className="bg-midnight-lighter/50 rounded-lg p-4 animate-pulse">
        <div className="h-4 bg-midnight-lighter rounded w-3/4" />
      </div>
    );
  }

  const photo = raffle.photos?.[0];
  const needsAction = handover && !handover.winnerConfirmed;

  return (
    <Link
      to={`/competition/${raffle.id}`}
      className={cn(
        'group flex items-center gap-4 rounded-xl p-3 border transition-all',
        needsAction
          ? 'bg-accent/5 border-accent/30 hover:border-accent/50'
          : 'bg-midnight-lighter/50 border-transparent hover:bg-midnight-lighter hover:border-primary/20'
      )}
    >
      {/* Photo with winner badge overlay */}
      <div className="relative w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-midnight-lighter">
        {photo && (
          <img
            src={photo}
            alt={raffle.title}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-midnight/80 to-transparent" />
        <div className="absolute bottom-1 left-1">
          <TrophyBadge />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-cream truncate group-hover:text-primary transition-colors">
          {raffle.title}
        </h4>
        <div className="flex items-center gap-2 mt-1">
          <HandoverStatusBadge handover={handover} />
        </div>
      </div>

      {/* Action indicator */}
      {needsAction && (
        <div className="shrink-0">
          <span className="flex items-center gap-1 text-xs font-medium text-accent">
            <AlertIcon className="w-4 h-4" />
            Action needed
          </span>
        </div>
      )}
    </Link>
  );
}

function HandoverStatusBadge({ handover }) {
  if (!handover) {
    return <Badge variant="success" size="sm">Completed</Badge>;
  }

  if (handover.status === 'completed') {
    return <Badge variant="success" size="sm">Collected</Badge>;
  }

  if (handover.winnerConfirmed && handover.sellerConfirmed) {
    return <Badge variant="success" size="sm">Both confirmed</Badge>;
  }

  if (handover.winnerConfirmed) {
    return <Badge variant="primary" size="sm">Waiting for seller</Badge>;
  }

  if (handover.sellerConfirmed) {
    return <Badge variant="warning" size="sm">Confirm collection</Badge>;
  }

  return <Badge variant="warning" size="sm">Arrange collection</Badge>;
}

function TrophyBadge() {
  return (
    <div className="bg-accent/90 rounded px-1.5 py-0.5 flex items-center gap-1">
      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
      </svg>
      <span className="text-2xs font-bold text-white">WON</span>
    </div>
  );
}

// Icons
function TrophyIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3h14M5 3a2 2 0 00-2 2v1a3 3 0 003 3h1m11-6a2 2 0 012 2v1a3 3 0 01-3 3h-1m-5 0v6m-3 0h6m-3-6a3 3 0 01-3-3V5a2 2 0 012-2h2a2 2 0 012 2v4a3 3 0 01-3 3z" />
    </svg>
  );
}

function AlertIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}
